/**
 * SSE event types emitted by the FastAPI /api/chat endpoint.
 *
 * These mirror the Python dataclasses in apps/api/ashi_api/agent/loop.py.
 * If you change one, change both.
 */

export interface TextDeltaEvent {
  type: "text_delta";
  text: string;
}

export interface ToolCallStartEvent {
  type: "tool_call_start";
  id: string;
  name: string;
  input_preview: string;
}

export interface ToolCallResultEvent {
  type: "tool_call_result";
  id: string;
  name: string;
  ok: boolean;
  summary: string;
}

export interface TurnCompleteEvent {
  type: "turn_complete";
  final_text: string;
  usage: {
    input_tokens?: number;
    output_tokens?: number;
    cache_read_input_tokens?: number;
    cache_creation_input_tokens?: number;
  };
  rounds: number;
}

export interface TurnErrorEvent {
  type: "turn_error";
  message: string;
}

export type ChatStreamEvent =
  | TextDeltaEvent
  | ToolCallStartEvent
  | ToolCallResultEvent
  | TurnCompleteEvent
  | TurnErrorEvent;

/**
 * Parse a single SSE event from the wire-format strings.
 *
 * The caller has already split the stream into events (between blank lines)
 * and extracted the `event: NAME` line and `data: JSON` line. We just shape
 * them into a typed object.
 */
export function parseSSEEvent(
  eventName: string,
  dataStr: string,
): ChatStreamEvent | null {
  let payload: unknown;
  try {
    payload = JSON.parse(dataStr);
  } catch {
    return null;
  }
  if (typeof payload !== "object" || payload === null) return null;
  const p = payload as Record<string, unknown>;

  switch (eventName) {
    case "text_delta":
      return { type: "text_delta", text: String(p.text ?? "") };
    case "tool_call_start":
      return {
        type: "tool_call_start",
        id: String(p.id ?? ""),
        name: String(p.name ?? ""),
        input_preview: String(p.input_preview ?? ""),
      };
    case "tool_call_result":
      return {
        type: "tool_call_result",
        id: String(p.id ?? ""),
        name: String(p.name ?? ""),
        ok: Boolean(p.ok),
        summary: String(p.summary ?? ""),
      };
    case "turn_complete":
      return {
        type: "turn_complete",
        final_text: String(p.final_text ?? ""),
        usage: (p.usage as TurnCompleteEvent["usage"]) ?? {},
        rounds: Number(p.rounds ?? 0),
      };
    case "turn_error":
      return { type: "turn_error", message: String(p.message ?? "Unknown error") };
    default:
      return null;
  }
}
